/*
 * Turning a Firebase auth rejection into something worth putting on screen.
 *
 * Screens used to print one fixed line whatever went wrong, so a throttled
 * resend read as "contact support" — sending someone to a human for a thing
 * that clears itself in a few minutes. Firebase already knows why it refused,
 * and its own copy is usually the most useful thing available; it just arrives
 * wrapped for a log rather than for a person:
 *
 *   Firebase: We have blocked all requests from this device… (auth/too-many-requests).
 *
 * So: our own wording for the handful of cases worth phrasing better, then
 * Firebase's own sentence with the wrapper taken off, and the caller's fixed
 * line only when neither leaves anything to say.
 */

/** Cases where Firebase's stock copy is accurate but unhelpfully alarming. */
const MESSAGES = {
  'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
  'auth/network-request-failed':
    'We could not reach the server. Check your connection and try again.'
}

/** `Firebase: ` prefix and the ` (auth/…)` the SDK appends to every message. */
const WRAPPER = [
  [/^Firebase:\s*/i, ''],
  [/\s*\(auth\/[^)]+\)\.?\s*$/i, '']
]

const unwrap = (message) =>
  WRAPPER.reduce((text, [pattern, to]) => text.replace(pattern, to), message).trim()

/**
 * What to show a user for `error`, or `fallback` when it says nothing useful.
 *
 * A bare error code is treated as saying nothing: some rejections arrive with
 * the code as their whole message, and `auth/internal-error` on screen is worse
 * than the generic line it would replace.
 */
export function firebaseErrorMessage (error, fallback) {
  const known = MESSAGES[error?.code]
  if (known) return known

  const message = typeof error?.message === 'string' ? unwrap(error.message) : ''
  if (!message || /^auth\/[a-z-]+\.?$/i.test(message)) return fallback

  return message
}
